import { useState } from "react";

import {
    getCellProperty,
    isAlphaNumericKey,
    keyCodes,
} from "@silevis/reactgrid";
import "./customCellStyle.css";
import "./dropdownCellTemplateStyle.css";

import {
    Autocomplete,
    Chip,
    ClickAwayListener,
    Grid,
    ListItem,
    TextField,
} from "@mui/material";
import HighlightCell from "./HighlightCell";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import { RATE_TYPE_FLAT_RATE, RATE_TYPE_PERCENTAGE } from "./constants";
import ContextMenuCellWrapper from "./ContextMenuCellWrapper";

export default class CustomDropdownCellTemplate {
    constructor(options) {
        this.options = options;
        this.variant = null;
        if (options.length > 0 && typeof options[0] == "object") {
            this.variant = "chip";
        }
    }

    getClassName(cell) {
        return "dropdown-cell";
    }

    getStyle() {
        return {
            overflow: "visible",
        };
    }

    getCompatibleCell(uncertainCell) {
        const text = getCellProperty(uncertainCell, "text", "string");
        const value = parseFloat(text);
        return { ...uncertainCell, text, value, enableEditMode: true };
    }

    handleKeyDown(cell, keyCode, ctrl, shift, alt) {
        if (
            (!ctrl && !alt && isAlphaNumericKey(keyCode)) ||
            keyCode == "onClick"
        ) {
            const char = String.fromCharCode(keyCode);
            const updatedCell = this.update(cell, {
                ...cell,
                text: char,
                value: parseFloat(char),
            });
            return { updatedCell, enableEditMode: true };
        }
        return {
            cell,
            enableEditMode:
                keyCode == keyCodes.POINTER || keyCode == keyCodes.ENTER,
        };
    }

    update(cell, cellToMerge) {
        return this.getCompatibleCell({
            ...cell,
            text: cellToMerge.text,
            enableEditMode: true,
        });
    }

    render(cell, isInEditMode, onCellChanged = (cell, commit) => {}) {
        return (
            <CustomDropdownCell
                cell={cell}
                variant={this.variant}
                options={this.options}
                onCommitCellChange={(newCellValue) => {
                    onCellChanged(
                        this.getCompatibleCell({
                            ...cell,
                            text: newCellValue,
                            value: parseFloat(newCellValue),
                        }),
                        true,
                    );
                }}
            />
        );
    }
}

const CustomDropdownCell = ({ cell, variant, options, onCommitCellChange }) => {
    let filteredOptions = options;
    if (cell.columnId == "rate_type" && cell.is_internal) {
        filteredOptions = options.filter(
            (option) => option.name != RATE_TYPE_FLAT_RATE,
        );
    }
    const [hover, setHover] = useState();
    const handleMouseEnter = () => {
        setHover(true);
    };
    const handleMouseLeave = () => {
        setHover(false);
    };
    return (
        <ContextMenuCellWrapper cell={cell}>
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <CustomAutocomplete
                    cell={cell}
                    variant={variant}
                    options={filteredOptions}
                    onCommitCellChange={onCommitCellChange}
                />
                <HighlightCell hover={hover} cell={cell} />
            </div>
        </ContextMenuCellWrapper>
    );
};

const CustomAutocomplete = ({ cell, variant, options, onCommitCellChange }) => {
    const [editing, setEditing] = useState(false);
    const [selectedValue, setSelectedValue] = useState(null);
    const [valueToCommit, setValueToCommit] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleClickText = () => {
        if (!cell.nonEditable) {
            setEditing(true);
        }
    };

    const commitChange = () => {
        const value = variant == "chip" ? valueToCommit.name : valueToCommit;
        onCommitCellChange(value);
        setSelectedValue(valueToCommit);
    };

    const handleAutocompleteChange = (event, newValue) => {
        setEditing(false);
        setValueToCommit(newValue);
        setDialogOpen(true);
    };

    const handleAutocompleteClear = (event, newValue, reason) => {
        if (reason == "clear") {
            onCommitCellChange(null);
            setSelectedValue(null);
        }
    };

    const handleInputKeyDown = (e) => {
        e.stopPropagation();
    };

    return (
        <>
            <ConfirmationDialog
                open={dialogOpen}
                closeDialog={() => setDialogOpen(false)}
                title={`Adjust rate type?`}
                callback={commitChange}
            >
                <p>
                    {
                        "Changing rate type will also change the rate, are you sure you want to proceed?"
                    }
                </p>
            </ConfirmationDialog>

            <Grid container alignItems={"center"} width={"100%"}>
                <Grid item width={"100%"}>
                    <ClickAwayListener
                        onClickAway={() => {
                            setEditing(false);
                        }}
                    >
                        {editing ? (
                            <Autocomplete
                                freeSolo
                                disableClearable
                                open
                                style={{ padding: 0 }}
                                value={selectedValue || cell.text}
                                sx={{ width: "100%" }}
                                disablePortal
                                options={options}
                                filterOptions={(options, state) => {
                                    return options.filter(
                                        (option) =>
                                            option.name != RATE_TYPE_PERCENTAGE,
                                    );
                                }}
                                getOptionLabel={(option) =>
                                    option.name || option
                                }
                                renderOption={(props, option, { selected }) => {
                                    return getOptionComponent(
                                        props,
                                        option,
                                        variant,
                                        cell.nonEditable,
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        sx={{ border: "none", outline: "none" }}
                                        autoFocus
                                        {...params}
                                        onPointerDown={(e) => {
                                            e.stopPropagation();
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            style: {
                                                fontSize: 12,
                                                border: "none",
                                                padding: 2,
                                                paddingLeft: 6,
                                            },
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { padding: 0, fontSize: 12 },
                                        }}
                                    />
                                )}
                                onKeyDown={handleInputKeyDown}
                                onChange={handleAutocompleteChange}
                                onInputChange={handleAutocompleteClear}
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    return variant == "chip"
                                        ? option.name == value
                                        : option == value;
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    cursor: cell.nonEditable
                                        ? "default"
                                        : "pointer",
                                }}
                                onClick={handleClickText}
                            >
                                {getOptionComponent(
                                    null,
                                    variant == "chip"
                                        ? options.find(
                                              (option) =>
                                                  option.name == cell.text,
                                          )
                                        : cell.text,
                                    variant,
                                    cell.nonEditable,
                                    editing,
                                )}
                            </div>
                        )}
                    </ClickAwayListener>
                </Grid>
            </Grid>
        </>
    );
};

const getOptionComponent = (
    props,
    option,
    variant,
    readOnly,
    editing = false,
) => {
    if (!option) {
        return <div style={{ minHeight: "20px", width: "100%" }} />;
    }

    const handlePointerDown = (event) => {
        editing && event.stopPropagation();
    };

    const chipColor = readOnly ? "secondary" : option.chipColor || "default";
    const style =
        variant == "chip"
            ? { padding: 5, margin: 0, justifyContent: "center" }
            : { padding: 0, margin: 4, fontSize: 12 };

    return (
        <ListItem
            {...props}
            key={option.name}
            style={style}
            onPointerDown={handlePointerDown}
        >
            {variant == "chip" ? (
                <Chip
                    size="small"
                    label={option.name}
                    color={chipColor}
                    sx={{
                        padding: 0,
                        margin: 0,
                        fontSize: 12,
                    }}
                />
            ) : (
                <div style={{ minHeight: "20px", width: "100%" }}>{option}</div>
            )}
        </ListItem>
    );
};
